import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FiCheck, FiPrinter, FiShoppingCart, FiLock, FiCornerUpLeft,
} from "react-icons/fi";

import SlideOverPanel from "../../../components/SlideOverPanel";
import { fmtQt } from "../../RevisorFerragens/lib/documento";
import { useAuth } from "../../../context/AuthContext";
import { useToast } from "../../../context/ToastContext";
import { formatarQuantidade, parseDinheiro } from "../../../utils/dinheiro";
import { SITUACAO_LABEL as SITUACAO_COMPRA_LABEL } from "../../../services/requisicoesCompraUi";
import {
  SITUACAO_LABEL, SITUACAO_TONE, marcarItemSeparado, solicitarCompraDeItem,
} from "../../../services/separacaoFerragens";
import {
  ChkAmbiente, ChkBox, ChkModulo, ChkNome, ChkQt, ChkRow, DField, Pill,
  ProgressoBarra, ProgressoTexto, ProgressoWrap,
} from "./styles";
import { Carregando } from "../_shared/Estados";

function dataHora(iso) {
  return iso ? new Date(iso).toLocaleString("pt-BR") : null;
}

function Campo({ label, children }) {
  if (children === null || children === undefined || children === "") return null;
  return (
    <DField>
      <b>{label}</b>
      <span>{children}</span>
    </DField>
  );
}

/** Itens da lista que contam pra separação, agrupados por ambiente > módulo. */
function agruparItens(lista) {
  const itens = (lista?.itens || []).filter(
    (it) => !it.oculto && Number(it.quantidade) > 0,
  );
  const ambientes = new Map();
  itens.forEach((it) => {
    const amb = it.ambiente_nome || "Ambiente";
    if (!ambientes.has(amb)) ambientes.set(amb, new Map());
    const modulos = ambientes.get(amb);
    const mod = it.modulo_nome || "";
    if (!modulos.has(mod)) modulos.set(mod, []);
    modulos.get(mod).push(it);
  });
  return { itens, ambientes };
}

/**
 * Checklist de separação — o almoxarife marca item a item enquanto separa.
 * Autosave OTIMISTA por item, com FILA por item (serializa PATCHes do mesmo
 * item — toggle rápido não reordena no servidor) e progresso reconciliado da
 * RESPOSTA do backend (total live: pega item manual que entre na lista
 * durante a separação). O estado persiste no backend — dá pra parar e retomar.
 */
function ChecklistSeparacao({ req, checks, setChecks, progresso, setProgresso, setEmVoo, onSolicitarCompra }) {
  const { request } = useAuth();
  const toast = useToast();
  const seqRef = useRef({});
  const filaRef = useRef({});

  const { itens, ambientes } = useMemo(() => agruparItens(req.lista), [req.lista]);
  const separadosLocal = itens.filter((it) => checks.has(it.id)).length;
  const total = progresso?.total ?? itens.length;
  const separados = progresso ? progresso.separados : separadosLocal;
  const pct = total > 0 ? (separados / total) * 100 : 0;

  function alternar(item) {
    const novo = !checks.has(item.id);
    const seq = (seqRef.current[item.id] || 0) + 1;
    seqRef.current[item.id] = seq;
    // Otimista: marca na hora.
    setChecks((prev) => {
      const n = new Set(prev);
      if (novo) n.add(item.id); else n.delete(item.id);
      return n;
    });
    setEmVoo((n) => n + 1);
    // Fila POR ITEM: o próximo PATCH deste item só sai quando o anterior
    // resolver — dois toggles rápidos não chegam fora de ordem no servidor.
    const anterior = filaRef.current[item.id] || Promise.resolve();
    filaRef.current[item.id] = anterior
      .then(() => marcarItemSeparado(request, req.id, item.id, novo))
      .then((res) => {
        // Só o toggle mais recente atualiza o progresso (anti-corrida).
        if (seqRef.current[item.id] === seq && res && typeof res.total === "number") {
          setProgresso({ separados: res.separados, total: res.total });
        }
      })
      .catch((err) => {
        if (seqRef.current[item.id] === seq) {
          setChecks((prev) => {
            const n = new Set(prev);
            if (novo) n.delete(item.id); else n.add(item.id);
            return n;
          });
          toast.error(err?.payload?.detalhe || err?.message || "Falha ao salvar o item.");
        }
      })
      .finally(() => setEmVoo((n) => Math.max(0, n - 1)));
  }

  return (
    <div>
      <ProgressoWrap>
        <ProgressoTexto>
          <span>Separação</span>
          <span>{separados} de {total} itens</span>
        </ProgressoTexto>
        <ProgressoBarra $pct={pct}><div /></ProgressoBarra>
      </ProgressoWrap>

      {Array.from(ambientes.entries()).map(([amb, modulos]) => (
        <div key={amb}>
          <ChkAmbiente>{amb}</ChkAmbiente>
          {Array.from(modulos.entries()).map(([mod, doModulo]) => (
            <div key={mod || "_sem"}>
              {mod ? <ChkModulo>{mod}</ChkModulo> : null}
              {doModulo.map((it) => {
                const on = checks.has(it.id);
                const meta = [it.fabricante, it.dimensoes].filter(Boolean).join(" · ");
                return (
                  <div key={it.id} style={{ display: "flex", alignItems: "stretch", gap: 6 }}>
                    <ChkRow
                      type="button"
                      $on={on}
                      onClick={() => alternar(it)}
                      aria-pressed={on}
                      aria-label={`${on ? "Desmarcar" : "Marcar"} ${it.nome}`}
                      style={{ flex: 1 }}
                    >
                      <ChkBox $on={on}>{on ? <FiCheck size={13} /> : null}</ChkBox>
                      <ChkNome $on={on}>
                        {it.nome}
                        {meta ? <span>{meta}</span> : null}
                      </ChkNome>
                      <ChkQt $on={on}>×{fmtQt(it.quantidade)}</ChkQt>
                    </ChkRow>
                    {/* Item em falta: TODO item tem a opcao (o almoxarife so
                        descobre a falta na hora de pegar na prateleira). */}
                    <button
                      type="button"
                      className="btn btn--secondary"
                      style={{ padding: "0 10px" }}
                      title={`Solicitar compra de ${it.nome}`}
                      aria-label={`Solicitar compra de ${it.nome}`}
                      onClick={() => onSolicitarCompra(it)}
                    >
                      <FiShoppingCart size={13} />
                    </button>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

export default function Detalhe({
  open, req, loading, onClose, onAcao, onImprimir,
  onSepararEImprimir, onReimprimirComprovante, podeGerir,
}) {
  const situacao = req?.situacao;
  // Checklist: estado local reinicializado do backend a cada carga do detalhe
  // (retomada — inclusive ao voltar da tela de impressão, que refetcha).
  const [checks, setChecks] = useState(() => new Set());
  const [progresso, setProgresso] = useState(null);
  const [emVoo, setEmVoo] = useState(0);
  useEffect(() => {
    setChecks(new Set(req?.itens_separados || []));
    setProgresso(req?.progresso_separacao || null);
  }, [req?.id, req?.itens_separados, req?.progresso_separacao]);

  // Requisicao de compra de item em falta (2026-08-04).
  const { request: requestApi } = useAuth();
  const toastDetalhe = useToast();
  const [itemCompra, setItemCompra] = useState(null);
  const [qtdCompra, setQtdCompra] = useState("");
  const [enviandoCompra, setEnviandoCompra] = useState(false);

  // Abre o pedido JA com a quantidade da linha no campo: o que vai para
  // Compras e o que esta na tela, nao o que estiver no banco na hora do envio
  // (o revisor pode ter mudado a lista depois que este painel abriu).
  function abrirCompra(item) {
    setItemCompra(item);
    setQtdCompra(formatarQuantidade(item.quantidade));
  }

  // Digitacao pt-BR pelo parser unico do projeto (ADR-0059): "1.500" = 1500,
  // "2,5" = 2,5. Com <input type=number> "1.500" chegava como 1,5.
  const qtdInterpretada = parseDinheiro(qtdCompra);
  const qtdValida = qtdInterpretada !== null && qtdInterpretada > 0
    && Number(qtdInterpretada.toFixed(3)) === qtdInterpretada;

  async function confirmarCompra() {
    if (!itemCompra || !qtdValida) return;
    setEnviandoCompra(true);
    try {
      const r = await solicitarCompraDeItem(requestApi, req.id, itemCompra.id, {
        quantidade: String(qtdInterpretada),
      });
      const onde = `requisição ${r.requisicao_compra_numero}`
        + (r.requisicao_compra_situacao ? ` · ${SITUACAO_COMPRA_LABEL[r.requisicao_compra_situacao] || r.requisicao_compra_situacao}` : "");
      toastDetalhe.success(
        r.linha_nova === false
          ? `Somado ao pedido anterior de ${itemCompra.nome}: total ×${formatarQuantidade(r.quantidade_total)} (${onde}).`
          : `Compra solicitada: ${itemCompra.nome} ×${formatarQuantidade(qtdInterpretada)} (${onde}).`,
      );
      setItemCompra(null);
      setQtdCompra("");
    } catch (err) {
      toastDetalhe.error(
        err?.payload?.erros?.detalhe || err?.payload?.detalhe || err.message || "Não foi possível solicitar a compra.",
      );
    } finally {
      setEnviandoCompra(false);
    }
  }

  const emRevisao = !!req?.em_revisao;
  const emSeparacao = situacao === "EM_SEPARACAO";
  // Gate de 100%: usa o progresso do SERVIDOR (live) — o backend revalida na
  // conclusão de qualquer forma. Bloqueia também enquanto há autosave em voo.
  const completo = !!progresso && progresso.total > 0 && progresso.separados >= progresso.total;
  const podeConcluir = completo && emVoo === 0;

  function pedirCancelar() {
    const motivo = window.prompt("Motivo do cancelamento:");
    if (motivo && motivo.trim()) onAcao("cancelar", motivo.trim());
  }

  function concluirComComprovante() {
    // Janela aberta SINCRONICAMENTE no clique — depois dos awaits o navegador
    // bloquearia o pop-up (Render free tem latência alta).
    const janela = window.open("", "_blank", "width=900,height=720");
    if (janela) {
      janela.document.write(
        '<title>Comprovante de separação</title>' +
        '<p style="font-family:sans-serif;padding:24px;color:#555">Gerando comprovante…</p>',
      );
    }
    onSepararEImprimir(req, janela);
  }

  const footer = req ? (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
      <button className="btn btn--secondary" onClick={() => onImprimir(req)}>
        <FiPrinter size={14} /> Imprimir documento
      </button>
      {podeGerir && !emRevisao && situacao === "PENDENTE" ? (
        <button className="btn btn--primary" onClick={() => onAcao("iniciar")}>Iniciar separação</button>
      ) : null}
      {podeGerir && !emRevisao && emSeparacao ? (
        <button
          className="btn btn--primary"
          disabled={!podeConcluir}
          title={
            podeConcluir
              ? undefined
              : emVoo > 0
                ? "Salvando itens… aguarde um instante."
                : "Separe todos os itens do checklist para concluir."
          }
          onClick={concluirComComprovante}
        >
          <FiCheck size={14} /> Marcar separada + comprovante
        </button>
      ) : null}
      {podeGerir && situacao === "SEPARADA" ? (
        <button className="btn btn--primary" onClick={() => onReimprimirComprovante(req)}>
          <FiPrinter size={14} /> Reimprimir comprovante
        </button>
      ) : null}
      {podeGerir && !emRevisao && emSeparacao ? (
        <button
          className="btn btn--secondary"
          title="Devolve a lista à fila sem perder o que você já conferiu"
          onClick={() => onAcao("voltar_pendentes")}
        >
          <FiCornerUpLeft size={14} /> Voltar aos pendentes
        </button>
      ) : null}
      {podeGerir && !emRevisao && (situacao === "PENDENTE" || emSeparacao) ? (
        <button className="btn btn--danger" onClick={pedirCancelar}>Cancelar ferragens</button>
      ) : null}
      {podeGerir && (situacao === "SEPARADA" || situacao === "CANCELADA") ? (
        <button className="btn btn--secondary" onClick={() => onAcao("reabrir")}>Reabrir</button>
      ) : null}
    </div>
  ) : null;

  return (
    <SlideOverPanel
      open={open}
      onClose={onClose}
      title={req ? req.numero : "Requisição de separação"}
      subtitle={req ? req.cliente_nome : ""}
      footer={footer}
      width="min(94vw, 780px)"
    >
      {loading ? (
        <Carregando linhas={3} colunas={2} />
      ) : !req ? (
        <div style={{ padding: 20, color: "var(--danger-text)" }}>
          Não foi possível carregar a requisição. Feche o painel e tente de novo.
        </div>
      ) : (
        <div>
          <div style={{ marginBottom: 14, display: "flex", gap: 8, alignItems: "center" }}>
            <Pill $tone={SITUACAO_TONE[situacao]}>{SITUACAO_LABEL[situacao] || situacao}</Pill>
            {emRevisao ? <Pill $tone="warning">Em revisão</Pill> : null}
          </div>

          {/* Bloqueio (2026-08-04): o solicitante esta corrigindo a lista. Sem
              este aviso o almoxarife separaria fisicamente uma lista que mudou
              debaixo dele — o backend recusa as acoes, mas a pessoa precisa
              saber POR QUE. */}
          {emRevisao ? (
            <div
              role="status"
              style={{
                display: "flex", gap: 10, alignItems: "flex-start",
                background: "var(--warning-bg)", border: "1px solid var(--warning-border)",
                color: "var(--warning-text)", borderRadius: 8,
                padding: "10px 12px", marginBottom: 14, fontSize: 13,
              }}
            >
              <FiLock size={16} style={{ flexShrink: 0, marginTop: 1 }} />
              <div>
                <b>Separação bloqueada — lista em revisão</b>
                <div style={{ marginTop: 2 }}>
                  {req.revisao_por_nome
                    ? `${req.revisao_por_nome} está corrigindo esta lista.`
                    : "O solicitante está corrigindo esta lista."}
                  {" "}Aguarde a finalização da revisão para retomar a separação.
                </div>
              </div>
            </div>
          ) : null}

          {emSeparacao && podeGerir && !emRevisao ? (
            <ChecklistSeparacao
              req={req}
              checks={checks}
              setChecks={setChecks}
              progresso={progresso}
              setProgresso={setProgresso}
              setEmVoo={setEmVoo}
              onSolicitarCompra={abrirCompra}
            />
          ) : (
            <>
              <Campo label="Cliente">{req.cliente_nome || "—"}</Campo>
              <Campo label="Projeto / ambientes">{req.projeto_resumo || "—"}</Campo>
              <Campo label="Itens">{req.total_itens}</Campo>
              <Campo label="Lista de origem">{req.lista_compras_titulo}</Campo>
            </>
          )}

          <div style={{ marginTop: emSeparacao ? 16 : 0 }}>
            <Campo label="Criada por">
              {req.criado_por_nome ? `${req.criado_por_nome} · ${dataHora(req.created_at)}` : dataHora(req.created_at)}
            </Campo>
            {req.iniciado_por_nome ? (
              <Campo label="Separação iniciada">{`${req.iniciado_por_nome} · ${dataHora(req.em_separacao_em)}`}</Campo>
            ) : null}
            {req.separado_por_nome ? (
              <Campo label="Separada por">{`${req.separado_por_nome} · ${dataHora(req.separado_em)}`}</Campo>
            ) : null}
            {req.motivo_cancelamento ? (
              <Campo label="Cancelamento">{`${req.motivo_cancelamento} · ${dataHora(req.cancelada_em)}`}</Campo>
            ) : null}
            {req.requisicao_compra_numero ? (
              <Campo label="Requisição de compra">{req.requisicao_compra_numero}</Campo>
            ) : null}
            {req.observacao ? (
              <Campo label="Observações">
                <span style={{ whiteSpace: "pre-wrap" }}>{req.observacao}</span>
              </Campo>
            ) : null}
          </div>
        </div>
      )}

      {/* Confirmacao da compra: quantidade em branco = a do proprio item (o
          caso comum e faltar o item inteiro). */}
      {itemCompra ? (
        <div
          role="dialog"
          aria-label="Solicitar compra"
          style={{
            position: "absolute", inset: 0, background: "var(--surface)",
            padding: 20, display: "flex", flexDirection: "column", gap: 14,
            zIndex: 5,
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: 16 }}>Solicitar compra</h3>
            <p style={{ margin: "6px 0 0", color: "var(--ink-3)", fontSize: 13 }}>
              {req.requisicao_compra_numero
                ? `O item entra na requisição de compra ${req.requisicao_compra_numero} desta separação. Se ela já tiver sido aprovada, volta para aprovação.`
                : "Abre a requisição de compra desta separação no setor de Compras."}
            </p>
          </div>

          <div style={{ fontSize: 14 }}>
            <b>{itemCompra.nome}</b>
            {[itemCompra.fabricante, itemCompra.dimensoes].filter(Boolean).length ? (
              <div style={{ color: "var(--ink-3)", fontSize: 12.5 }}>
                {[itemCompra.fabricante, itemCompra.dimensoes].filter(Boolean).join(" · ")}
              </div>
            ) : null}
          </div>

          <label style={{ display: "flex", flexDirection: "column", gap: 6, fontSize: 13 }}>
            Quantidade a comprar
            <input
              id="qtd-compra-falta"
              type="text"
              inputMode="decimal"
              autoComplete="off"
              className="input"
              value={qtdCompra}
              onChange={(e) => setQtdCompra(e.target.value)}
              aria-invalid={!qtdValida}
            />
            <span style={{ fontSize: 12, color: qtdValida ? "var(--ink-3)" : "var(--danger-text)" }}>
              {qtdValida
                ? `Vai pedir ×${formatarQuantidade(qtdInterpretada)} · na lista deste módulo: ×${fmtQt(itemCompra.quantidade)}`
                : "Informe uma quantidade maior que zero, com até 3 casas (ex.: 12 ou 2,5)."}
            </span>
          </label>

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: "auto" }}>
            <button
              type="button"
              className="btn btn--secondary"
              disabled={enviandoCompra}
              onClick={() => { setItemCompra(null); setQtdCompra(""); }}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="btn btn--primary"
              disabled={enviandoCompra || !qtdValida}
              onClick={confirmarCompra}
            >
              {enviandoCompra ? "Enviando…" : "Enviar para Compras"}
            </button>
          </div>
        </div>
      ) : null}
    </SlideOverPanel>
  );
}
